import folderIdToName from 'owa-session-store/lib/utils/folderIdToName';
import type MailListViewState from 'mini-mail-list-store/lib/store/schema/MailListViewState';
import safePerformanceMarkEnd from 'mini-performance/lib/utils/safePerformanceMarkEnd';
import { getBootType } from 'owa-config';
import { getTimeFromFetchStart } from 'mini-performance/lib/utils/getTimeFromFetchStart';
import { MAIL_LIST_RENDERED } from 'mini-app-constants/lib/commonEventSignalConstants';
import { PerformanceDatapoint } from 'owa-analytics';
import { signalWorkflowEvent } from 'mini-workflow-manager';
import { type TableQueryType } from 'mini-mail-list-store/lib/store/schema/TableQuery';
import { trace } from 'owa-trace';
import { triggerSearchSignals } from '../../utils/telemetry/triggerSearchSignals';
import type TableView from 'mini-mail-list-store/lib/store/schema/TableView';
import type MailFolderTableQuery from 'mini-mail-list-store/lib/store/schema/MailFolderTableQuery';
import type DistinguishedFolderIdName from 'owa-service/lib/contract/DistinguishedFolderIdName';

let firstLoadComplete: boolean;

export const measureMailListInitialLoadPerformance = async (
    tableView: TableView,
    loadState: MailListViewState,
    source: string
) => {
    try {
        if (!tableView || loadState === 1) {
            // We don't have the right info to log a render perf, so don't do anything
            return;
        }

        // Trigger, Search workflow related signals for tracking reliability
        if (tableView.tableQuery.type === 1) {
            triggerSearchSignals(tableView, loadState);
            return;
        }

        //Trigger, MailListRendered to track the LSR for app load times
        requestAnimationFrame(() => {
            const beforePaintTime = getTimeFromFetchStart();
            setTimeout(() => {
                signalWorkflowEvent(MAIL_LIST_RENDERED, {
                    afterPaintTime: getTimeFromFetchStart(),
                    beforePaintTime,
                });
            }, 0);
        });

        // Trigger, PerceivedPerformance: MailListContentRender when we switch folders or exit search
        // Save this perf marker for the first load so that they are tracked by PAS
        const measure = safePerformanceMarkEnd('SelectFolderNodeToMailListContentRender');
        if (measure) {
            const folderTableQuery = tableView.tableQuery as MailFolderTableQuery;
            const folderId = folderTableQuery.folderId as DistinguishedFolderIdName;
            const distinguishedFolderId = folderId && folderIdToName(folderId);
            const mailListPerceivedPerformanceDataPoint = new PerformanceDatapoint(
                'PerceivedPerformance: MailListContentRender',
                { isCore: true }
            );

            let bootType: string;
            if (!firstLoadComplete) {
                try {
                    bootType = await getBootType();
                } catch {
                    // fail silently
                }
            }

            mailListPerceivedPerformanceDataPoint.addCustomData({
                startTime: Math.round(measure.startTime),
                source: source,
                loadingState: loadState,
                tableQueryType: folderTableQuery ? folderTableQuery.type : 'No Type',
                distinguishedFolderId: distinguishedFolderId,
                firstLoadCompleteTime: firstLoadComplete
                    ? ''
                    : new Date().getTime() - (window.performance?.timing?.fetchStart ?? 0),
                bootType: firstLoadComplete ? '' : bootType,
            });
            mailListPerceivedPerformanceDataPoint.end(Math.round(measure.duration));

            if (!firstLoadComplete) {
                firstLoadComplete = true;
            }
        }
    } catch (error) {
        trace.warn(`Error in measureInitialLoadPerformance for MailList: ${error}`);
    }
};
